import { getStartParamCached } from './parseStartParam';

/**
 * Get venue slug from multiple sources (priority order):
 * 1. Telegram Mini App start_param (from bot link like t.me/bot?start=krasivoekafe)
 *    - Now supports format: venueSlug_refTelegramId for referrals
 * 2. URL query parameter ?venue=XXX
 * 3. URL hash #tgWebAppStartParam=XXX
 * 4. localStorage (saved from previous session)
 * 5. Fallback to 'demo'
 */
export function getVenueSlug(): string {
  // 1. Try Telegram start_param (highest priority)
  // Now uses parseStartParam to extract venue slug from combined format
  try {
    const parsed = getStartParamCached();
    if (parsed.venueSlug) {
      console.log('[venueSlug] Got from Telegram start_param (parsed):', parsed.venueSlug);
      // Save to localStorage for future sessions
      try {
        localStorage.setItem('lunit_venue_slug', parsed.venueSlug);
      } catch (e) {
        // localStorage not available
      }
      return parsed.venueSlug;
    }
    // If start_param was a legacy referral (only digits), venueSlug will be null
    // Fall through to other sources
    if (parsed.referralCode) {
      console.log('[venueSlug] start_param is legacy referral, looking for venue elsewhere');
    }
  } catch (e) {
    // Telegram not available
  }

  // 2. Try URL query parameter ?venue=XXX
  const params = new URLSearchParams(window.location.search);
  const urlVenue = params.get('venue');
  if (urlVenue) {
    console.log('[venueSlug] Got from URL param ?venue=:', urlVenue);
    return urlVenue;
  }

  // 3. Try URL hash #tgWebAppStartParam=XXX (some Telegram versions)
  const hash = window.location.hash;
  if (hash) {
    const hashParams = new URLSearchParams(hash.slice(1));
    const hashVenue = hashParams.get('tgWebAppStartParam');
    if (hashVenue) {
      console.log('[venueSlug] Got from URL hash tgWebAppStartParam:', hashVenue);
      return hashVenue;
    }
  }

  // 4. Try localStorage (saved from previous session)
  try {
    const savedSlug = localStorage.getItem('lunit_venue_slug');
    if (savedSlug) {
      console.log('[venueSlug] Got from localStorage:', savedSlug);
      return savedSlug;
    }
  } catch (e) {
    // localStorage not available
  }

  // 5. Fallback
  console.log('[venueSlug] Using fallback: demo');
  return 'demo';
}

// Cache the slug once determined (it won't change during session)
// IMPORTANT: Only cache if we got a real slug (not 'demo' fallback)
// to avoid race condition where cache is set before Telegram SDK is ready
let cachedSlug: string | null = null;

export function getVenueSlugCached(): string {
  // If we have a cached slug that's not the fallback, return it
  if (cachedSlug !== null && cachedSlug !== 'demo') {
    return cachedSlug;
  }

  const slug = getVenueSlug();

  // Only cache if we got a real slug (not fallback)
  if (slug !== 'demo') {
    console.log('[venueSlug] Caching valid slug:', slug);
    cachedSlug = slug;
  } else {
    console.log('[venueSlug] Not caching fallback "demo" (SDK may not be ready)');
  }

  return slug;
}
